<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'PrintInvoices',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
    formName: {
      type: String,
      required: false,
      default: 'printInvoices',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const labelAlign = ref('right');
  const payload = ref({
    copies: 1,
    printer: null,
    target: 'remote',
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    payload: payload.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      {
        name: 'payload',
        cache: { type: 'local', key: 'PrintInvoices.payload' },
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      labelAlign,
      payload,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('PrintInvoices|Title', 'Print Invoices'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );
  watch(
    () => props.formName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_3xg0h8819_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_8564662037462133_closeComponent_kjphrgk38_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_spzn6oeeu_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: payload.value,
      },
    };
  };

  const act_7315092382398562_closeModal_3xg0h8819 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_3xg0h8819_getActionArgs,
    });
  const inst_evh_7315092382398562_close_6lugqmk8c =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_3xg0h8819,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_6lugqmk8c],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_rrtbbh4r1 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_kheufb2sb =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_rrtbbh4r1,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_kheufb2sb],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_kjphrgk38 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_8564662037462133_closeComponent_kjphrgk38_getActionArgs,
    });
  const inst_evh_8564662037462133_close_uodqf118k =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_kjphrgk38,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_uodqf118k],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_spzn6oeeu =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_spzn6oeeu_getActionArgs,
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit_ifd67wikk =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_spzn6oeeu,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_ifd67wikk],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_6z2tqurcr =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_blce2eh2u =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_6z2tqurcr,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_blce2eh2u],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_6lugqmk8c,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_kheufb2sb,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_uodqf118k,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_submit_ifd67wikk,
    submit: inst_evh_1040167445267876_submit_ifd67wikk,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_blce2eh2u,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_6lugqmk8c = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_6lugqmk8c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_kheufb2sb = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_kheufb2sb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_uodqf118k = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_uodqf118k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_ifd67wikk = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_ifd67wikk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_blce2eh2u = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_blce2eh2u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="PrintInvoices"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="copies"
          :label="pgettext('InvoiceSync', 'Copies')"
          :labelAlign="labelAlign"
          :label-cols-md="4"
          :content-cols-md="3"
        >
          <ItpFormInput
            type="number"
            name="copies"
            v-model="payload.copies"
            required
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="printer"
          :label="pgettext('InvoiceSync', 'Printer')"
          :labelAlign="labelAlign"
          :label-cols-md="4"
          :content-cols-md="8"
        >
          <UcPrinterSelector :item="payload" path="printer" clearable>
          </UcPrinterSelector>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('PrintInvoices|Submit', 'Print')"
          :disabled="!forms[formName] || !forms[formName].submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-print"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
